/**
 * HTTP istemcisi.
 *
 * Sözleşme kuralları — docs/standards/api-standard.md:
 * - Her istek `X-Correlation-ID` taşır (istek → job → log zinciri).
 * - Mutasyonlar `Idempotency-Key` taşır (mükerrer sipariş/puan koruması).
 * - Yalnızca güvenli ve açıkça izin verilen istekler tekrar denenir.
 */
import { isApiError, type ApiResponse, type PaginatedResponse } from '@restaurant/contracts'
import { ApiError, NETWORK_ERROR_STATUS, defaultErrorCode, parseRetryAfter } from './errors'
import { ulid } from './ulid'

export interface ApiClientOptions {
  readonly baseUrl: string
  /** Cihaz/oturum token'ı. Çevrimdışı dönemde de geçerli kalan dayanıklı kimlik. */
  readonly getToken?: () => string | undefined
  /** Sanctum SPA modunda XSRF çerezinden okunan CSRF token'ı. */
  readonly getCsrfToken?: () => string | undefined
  /** Milisaniye. 0 veya negatif değer zaman aşımını devre dışı bırakır. */
  readonly timeoutMs?: number
  /** Tekrar denenebilir hatalarda en fazla kaç ek deneme yapılacağı. */
  readonly maxRetries?: number
  /** Çerez tabanlı oturum kullanılıyorsa `include` olmalıdır. */
  readonly credentials?: RequestCredentials
}

export interface RequestOptions {
  /**
   * Mutasyon isteklerinde tekrar koruması anahtarı.
   * Verilmezse istemci üretir; çevrimdışı kuyruk **kendi ULID'ini geçirmelidir**,
   * aksi halde tekrar oynatmada yeni anahtar üretilir ve koruma çalışmaz.
   */
  readonly idempotencyKey?: string
  readonly correlationId?: string
  readonly signal?: AbortSignal
  readonly headers?: Readonly<Record<string, string>>
  /**
   * Bu isteğin otomatik tekrar denenmesine izin verilir mi?
   *
   * GET için varsayılan `true`. Mutasyonlar için varsayılan `false`:
   * `Idempotency-Key` gönderilse bile otomatik tekrar, sunucu tarafı koruma
   * doğrulanmadan mükerrer kayıt riskini taşır. Çağıran taraf bilinçli olarak
   * açar (bkz. R-05).
   */
  readonly retry?: boolean
}

/** `download()` sonucu: gövde ve sunucunun bildirdiği dosya adı. */
export interface DownloadedFile {
  readonly blob: Blob
  /** `Content-Disposition` okunamazsa `null`; çağıran kendi adını verir. */
  readonly filename: string | null
}

const DEFAULT_TIMEOUT_MS = 15_000
const DEFAULT_MAX_RETRIES = 2
const RETRY_BASE_DELAY_MS = 300
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export class ApiClient {
  private readonly baseUrl: string

  constructor(private readonly options: ApiClientOptions) {
    // Yol birleştirmede çift eğik çizgi oluşmasını engelle.
    this.baseUrl = options.baseUrl.replace(/\/+$/, '')
  }

  get<TData>(path: string, options: RequestOptions = {}): Promise<ApiResponse<TData>> {
    return this.send<ApiResponse<TData>>('GET', path, undefined, options)
  }

  list<TItem>(path: string, options: RequestOptions = {}): Promise<PaginatedResponse<TItem>> {
    return this.send<PaginatedResponse<TItem>>('GET', path, undefined, options)
  }

  post<TData>(
    path: string,
    body: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<TData>> {
    return this.send<ApiResponse<TData>>('POST', path, body, options)
  }

  put<TData>(
    path: string,
    body: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<TData>> {
    return this.send<ApiResponse<TData>>('PUT', path, body, options)
  }

  patch<TData>(
    path: string,
    body: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<TData>> {
    return this.send<ApiResponse<TData>>('PATCH', path, body, options)
  }

  delete<TData>(path: string, options: RequestOptions = {}): Promise<ApiResponse<TData>> {
    return this.send<ApiResponse<TData>>('DELETE', path, undefined, options)
  }

  /**
   * İkili içerik indirir (QR görseli, baskı PDF'i).
   *
   * Ayrı bir metottur çünkü yanıt JSON değildir; ama **aynı** istek
   * makinesini kullanır. Doğrudan `fetch` ile indirmek, korelasyon
   * kimliğini, kimlik başlığını ve hata sınıflandırmasını bu tek yolun
   * dışına çıkarırdı.
   *
   * Hata yanıtları yine JSON'dur ve `ApiError`'a çevrilir: sunucu zarfı
   * yolun `api/*` olmasına göre seçer, `Accept` başlığına göre değil.
   */
  download(path: string, options: RequestOptions = {}): Promise<DownloadedFile> {
    return this.send<DownloadedFile>('GET', path, undefined, options, readAsFile)
  }

  private async send<TResult>(
    method: HttpMethod,
    path: string,
    body: unknown,
    options: RequestOptions,
    decode: ResponseDecoder<TResult> = readAsJson,
  ): Promise<TResult> {
    const isSafe = SAFE_METHODS.has(method)
    const retryAllowed = options.retry ?? isSafe
    const maxAttempts = retryAllowed ? (this.options.maxRetries ?? DEFAULT_MAX_RETRIES) + 1 : 1

    // Idempotency anahtarı denemeler arasında SABİT kalmalı; her denemede
    // yenilenirse sunucu tarafı koruma devre dışı kalır.
    const idempotencyKey = isSafe ? undefined : (options.idempotencyKey ?? ulid())
    const correlationId = options.correlationId ?? ulid()

    let lastError: ApiError | undefined

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await this.attempt<TResult>(
          method,
          path,
          body,
          options,
          idempotencyKey,
          correlationId,
          decode,
        )
      } catch (error: unknown) {
        if (!(error instanceof ApiError) || !error.isRetryable || attempt === maxAttempts) {
          throw error
        }

        lastError = error
        await delay(retryDelayMs(attempt, error))
      }
    }

    // Döngü her zaman ya döner ya fırlatır; buraya yalnızca mantık hatasıyla gelinir.
    throw (
      lastError ??
      new ApiError({
        status: NETWORK_ERROR_STATUS,
        code: 'network_error',
        message: 'İstek tamamlanamadı.',
      })
    )
  }

  private async attempt<TResult>(
    method: HttpMethod,
    path: string,
    body: unknown,
    options: RequestOptions,
    idempotencyKey: string | undefined,
    correlationId: string,
    decode: ResponseDecoder<TResult>,
  ): Promise<TResult> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Correlation-ID': correlationId,
      ...options.headers,
    }

    /*
     * Çok parçalı gövde JSON DEĞİL ve başlığını tarayıcı yazmalı.
     *
     * `Content-Type` elle yazılırsa çok parçalı gövdenin sınır dizesi
     * (`boundary`) eksik kalıyor ve sunucu gövdeyi hiç ayrıştıramıyor. Menü
     * görseli yüklemenin çalışmama sebebi tam olarak buydu: istemci her
     * gövdeyi `JSON.stringify` ile geçiriyordu ve bir `FormData` oradan
     * `{}` olarak çıkıyordu.
     */
    const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData

    if (body !== undefined && !isMultipart) {
      headers['Content-Type'] = 'application/json'
    }

    if (idempotencyKey !== undefined) {
      headers['Idempotency-Key'] = idempotencyKey
    }

    const token = this.options.getToken?.()
    if (token !== undefined && token !== '') {
      headers['Authorization'] = `Bearer ${token}`
    }

    const csrfToken = this.options.getCsrfToken?.()
    if (csrfToken !== undefined && csrfToken !== '' && !SAFE_METHODS.has(method)) {
      headers['X-XSRF-TOKEN'] = csrfToken
    }

    const controller = new AbortController()
    const timeoutMs = this.options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    const timeoutId =
      timeoutMs > 0
        ? setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs)
        : undefined

    // Çağıranın iptali ile zaman aşımı iptalini birleştir.
    const onExternalAbort = () => controller.abort(options.signal?.reason)
    options.signal?.addEventListener('abort', onExternalAbort, { once: true })

    let response: Response
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? null : isMultipart ? (body as FormData) : JSON.stringify(body),
        signal: controller.signal,
        ...(this.options.credentials !== undefined
          ? { credentials: this.options.credentials }
          : {}),
      })
    } catch (cause: unknown) {
      // Çağıranın açık iptali hata değildir; olduğu gibi yukarı taşınır.
      if (options.signal?.aborted === true) {
        throw cause
      }

      throw new ApiError({
        status: NETWORK_ERROR_STATUS,
        code: 'network_error',
        message: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.',
      })
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
      options.signal?.removeEventListener('abort', onExternalAbort)
    }

    if (!response.ok) {
      throw await toApiError(response)
    }

    return decode(response)
  }
}

/** Yanıt gövdesini çağıranın beklediği biçime çevirir. */
type ResponseDecoder<TResult> = (response: Response) => Promise<TResult>

async function readAsJson<TResult>(response: Response): Promise<TResult> {
  if (response.status === 204) {
    return { data: null } as TResult
  }

  const payload: unknown = await response.json().catch(() => null)
  if (payload === null) {
    throw new ApiError({
      status: response.status,
      code: 'server_error',
      message: 'Sunucu yanıtı okunamadı.',
    })
  }

  // Gövde `unknown` olarak ayrıştırılır ve çağıranın beklediği tipe daraltılır;
  // `any` kullanılmaz.
  return payload as TResult
}

async function readAsFile(response: Response): Promise<DownloadedFile> {
  return {
    blob: await response.blob(),
    filename: parseFilename(response.headers.get('Content-Disposition')),
  }
}

/**
 * `Content-Disposition` başlığından dosya adını okur.
 *
 * Ad **sunucudan** gelir; istemcide üretmek, aynı dosyanın iki yerde farklı
 * adlandırılmasına yol açardı. Okunamazsa `null` döner ve çağıran kendi
 * varsayılanını kullanır.
 */
function parseFilename(header: string | null): string | null {
  if (header === null) {
    return null
  }

  const match = /filename="?([^";]+)"?/i.exec(header)

  return match?.[1] ?? null
}

async function toApiError(response: Response): Promise<ApiError> {
  const payload: unknown = await response.json().catch(() => null)
  const retryAfter = parseRetryAfter(response.headers.get('Retry-After'))

  if (isApiError(payload)) {
    return new ApiError({
      status: response.status,
      code: payload.code,
      message: payload.message,
      ...(payload.errors !== undefined ? { errors: payload.errors } : {}),
      ...(payload.correlationId !== undefined ? { correlationId: payload.correlationId } : {}),
      ...(retryAfter !== undefined ? { retryAfterSeconds: retryAfter } : {}),
    })
  }

  // Laravel doğrulama yanıtı (`{ message, errors }`) — henüz kod alanı yoksa.
  const legacy = payload as { message?: unknown; errors?: unknown } | null
  const message =
    typeof legacy?.message === 'string'
      ? legacy.message
      : `İstek başarısız (HTTP ${response.status}).`

  return new ApiError({
    status: response.status,
    code: defaultErrorCode(response.status),
    message,
    ...(isFieldErrors(legacy?.errors) ? { errors: legacy.errors } : {}),
    ...(retryAfter !== undefined ? { retryAfterSeconds: retryAfter } : {}),
  })
}

function isFieldErrors(value: unknown): value is Record<string, string[]> {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  return Object.values(value).every(
    (entry) => Array.isArray(entry) && entry.every((item) => typeof item === 'string'),
  )
}

/**
 * Üstel geri çekilme + jitter.
 *
 * Jitter olmadan, kesintiden çıkan tüm POS cihazları aynı anda yeniden dener ve
 * sunucuyu ikinci kez düşürür (thundering herd).
 * 429 yanıtında sunucunun bildirdiği süreye uyulur.
 */
export function retryDelayMs(attempt: number, error: ApiError): number {
  if (error.retryAfterSeconds !== undefined) {
    return error.retryAfterSeconds * 1000
  }

  const exponential = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1)
  const jitter = Math.random() * RETRY_BASE_DELAY_MS
  return Math.round(exponential + jitter)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
